import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Navigate } from "react-router-dom";
import {
  buildScormLaunchResourceUrl,
  getLmsOrigin,
} from "@/components/lms/lmsScormRuntime";
import { cleanupExpiredScormLaunchData } from "@/components/lms/scormLaunchSession";
import { createPromiseRequest } from "@/components/lms/lms.utils";

interface ScormLaunchSessionResponse {
  launch_token: string;
  enrolment_id: string;
}

const fullViewportSx = {
  alignItems: "center",
  backgroundColor: "#fff",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  justifyContent: "center",
  minHeight: "100vh",
  p: 3,
  textAlign: "center",
};

export default function LmsScormLaunch() {
  const { t } = useTranslation();
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const subdomain = window.location.hostname.split(".")[0];
  const startedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (
      subdomain !== "portal" ||
      !enrolmentId ||
      startedRef.current
    )
      return;
    startedRef.current = true;

    const launch = async () => {
      try {
        cleanupExpiredScormLaunchData();
        const response = await createPromiseRequest<{
          data: ScormLaunchSessionResponse;
        }>({
          entity: "enrolment",
          entityId: enrolmentId,
          method: "post",
          additionalRouteParts: { start_scorm_session: "/" },
          disableGlobalSpinner: true,
        });
        const session = response.data;
        // the lms proxy resolves the manifest entry server-side and redirects; the portal must not fetch lms.* cross-origin
        window.location.replace(
          getLmsOrigin() +
            buildScormLaunchResourceUrl({
              enrolmentId: session.enrolment_id,
              launchToken: session.launch_token,
              resourcePath: "",
            })
        );
      } catch {
        setErrorMessage(t("scorm_launch_error"));
      }
    };

    void launch();
  }, [enrolmentId, subdomain, t]);

  if (subdomain !== "portal") {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={fullViewportSx} data-testid="lms-scorm-launch">
      {errorMessage ? (
        <Typography role="alert">{errorMessage}</Typography>
      ) : (
        <>
          <CircularProgress />
          <Typography>{t("scorm_launch_preparing")}</Typography>
        </>
      )}
    </Box>
  );
}
